<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Počty ulic</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/pocty_ulic.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Rejstřík ulic / Zdroj: PRAHA. Podrobný plán hlavního
                        města 1:15 000, Praha 1948</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Přehled vývoje počtu pražských ulic od středověku po
                    současnost, včetně důvodů, proč je jejich přesné určení
                    problematické. Text doplňují aktuální statistiky a srovnání
                    s jinými městy.
                </p>

                <p class="mb-5">
                    Počet pražských ulic je těžké zjistit. Problémem je totiž
                    nejen absence úředních seznamů ulic, ale také – i pokud je
                    máme k dispozici – jejich spolehlivost. Často totiž
                    zaznamenaly některé ulice dvakrát (i vícekrát), pokud ležely
                    na dvou (či více) katastrálních územích. Jindy zas byly
                    zapsány ulice, které byly teprve naplánovány a jejich
                    skutečná výstavba se uskutečnila až o mnoho let později
                    (nebo také vůbec ne).
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/pocty_ulic_graf.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Vývoj počtu ulic od 15. století do současnosti. /
                        Zdroj: Marek a Barbora Lašťovkovi</span
                    >
                </div>

                <p class="font-bold">Do roku 1922</p>
                <p class="mb-5">
                    Vývoj počtu ulic od raného středověku prakticky nelze
                    odhadovat, dá se ale předpokládat, že byl víceméně pozvolný
                    a souvisel s rozvojem jednotlivých pražských měst. Po
                    konsolidaci Nového Města pražského zhruba okolo roku 1400 se
                    lze domnívat, že všechna pražská města měla okolo 200 ulic.
                    Kolem poloviny 19. století, tedy v době před pražskou
                    asanací, před připojením rozvíjejících se předměstí, a před
                    masivní výstavbou činžovních domů, měla tehdejší Praha (tj.
                    Staré Město, Nové Město, Josefov, Malá Strana, Hradčany)
                    něco málo přes <span class="font-bold">300</span> ulic.
                    Vyhláška Rady městské král. hlav. města Prahy z roku 1894
                    jmenuje jednotlivé pražské ulice, a to 91 na Starém Městě,
                    137 na Novém Městě, 51 na Malé Straně, 21 na Hradčanech, 20
                    na Josefově, 12 na Vyšehradě a 52 v Holešovicích-Bubnech.
                    Celkem tedy
                    <span class="font-bold">384</span>
                    názvů ulic, náměstí a sadů (např. ale Karlův most, který v
                    té době již název měl, zde zapsán není). V roce 1901 byla
                    pak k Praze připojena Libeň a s ní zhruba stovka dalších
                    ulic. Před vznikem Velké Prahy v roce 1922 tedy mělo město
                    asi <span class="font-bold">500</span> ulic a náměstí.
                </p>

                <p class="font-bold">1922–1945</p>
                <p class="mb-5">
                    Zásadním administrativním rozšířením území o 37 okolních
                    obcí zákonem o Velké Praze nabralo město nárazově zhruba
                    tisícovku ulic (rozloha území se zvětšila zhruba 8x).
                    Bohužel stále chybí jakýkoliv seznam názvů veřejných
                    prostranství, ať již úřední či neúřední, nejen bezprostředně
                    po vzniku Velké Prahy, ale i po celé období 1. čs.
                    republiky. Nicméně vzhledem ke skutečnosti, že mezi lety
                    1925–1938 bylo v Praze nově pojmenováno cca 1000 veřejných
                    prostranství, dostáváme se (odečtem od pozdějších údajů z
                    roku 1938) v roce 1922 na počet prostranství pohybující se
                    kolem <span class="font-bold">1500</span>. V roce 1938
                    ukazuje rejstřík ulic orientačního plánu hl. m. Prahy číslo
                    <span class="font-bold">2575</span>. Následný úřední seznam
                    z roku 1940 však vypočítává pražských veřejných prostranství
                    asi o 100 méně. Nesrovnalosti lze hledat ve skutečnosti, že
                    jak plán z roku 1938, tak i úřední okupační seznam roku 1940
                    zaznamenávaly i ještě fyzicky neexistující ulice.
                </p>

                <p class="font-bold">Od roku 1945</p>
                <p class="mb-5">
                    Publikace Ulicemi města Prahy z roku 1958 eviduje téměř
                    <span class="font-bold">2800</span>
                    názvů ulic, náměstí, sadů, mostů, ostrovů a nábřeží. Do roku
                    1965 jich pak přibylo (zejména novou výstavbou, částečně i
                    připojením obcí Ruzyně a Čimic v roce 1960) dalších 500.
                    Před dvěma největšími a doposud posledními územními
                    rozšířeními měla Praha tedy kolem
                    <span class="font-bold">3330</span> ulic. V roce 1968 pak
                    bylo připojeno 21 sousedních obcí a dalších 29 obcí v roce
                    1974. V prvním případě přibylo Praze 1126 ulic, v druhém
                    případě 1211 ulic. Po těchto dvou jednorázových rozšířeních
                    měla Praha více než
                    <span class="font-bold">5600</span> ulic. Další nové ulice
                    pak přibývaly zejména sídlištní výstavbou ve 2. polovině 70.
                    let a v 80. letech 20. století a do roku 1998 jich bylo přes
                    6500. V červenci 2020 bylo evidováno v úředním Registru
                    územní identifikace adres a nemovitostí
                    <span class="font-bold">7786</span> názvů pražských
                    veřejných prostranství, v prosinci 2021
                    <span class="font-bold">7821</span> a k dnešnímu dni je zde
                    evidováno
                    <span class="font-bold">
                        {{ pocetUlic }}
                    </span>
                    názvů.
                </p>
                <p class="mb-10 sm:mb-25">
                    Pro srovnání uvádíme přibližné počty ulic dalších velkých
                    měst v České republice (dle RÚIAN, rok 2020 a 26. 4. 2025):
                    Ostrava 1937 (1935), Brno 1887 (1906), Plzeň 1229 (1251),
                    Liberec 1009 (1010), Olomouc 724 (751). Z nejbližších
                    evropských velkoměst evidovala roku 2020 Vídeň 6842 ulic
                    (při o 16% menší rozloze, ale větším počtu obyvatel než
                    Praha) a Mnichov 6000 ulic (při o 40% menší rozloze, ale o
                    málo větším počtu obyvatel než Praha).
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/lokalni-nazvoslovne-systemy"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2"
                                >Lokální názvoslovné systémy</span
                            >
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/vyvoj-oznaceni-ulic-v-terenu"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 mr-2"
                                >Vývoj označení ulic v terénu</span
                            >
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts">
const { serverUri } = useDataLogic()

const responseData = ref<any>({})

const pocetUlic = ref(0)

//onMounted
onMounted(async () => {
    StreetLength()
})

async function StreetLength() {
    //isLoading.value = true // Start loading

    const data = await $fetch("select-heslo-all/", {
        baseURL: serverUri(),
    })
    responseData.value = data as any[]

    responseData.value = responseData.value.filter((item: any) => {
        return item["typ-hesla-ulice"] === "RUIAN"
    })
    pocetUlic.value = responseData.value.length
    // console.log(pocetUlic.value)
}
</script>

<style scoped></style>
